// src/components/Onboarding/Logo.js

import React from 'react';
import logoDark from '../../images/matchmedic_logo_dark.png';
import logoWhite from '../../images/matchmedic_logo_white.png';

/**
 * LOGO COMPONENT
 * 
 * Theme-aware logo component that automatically switches between dark and white logos
 * based on the current theme. Optimized for onboarding flow header placement.
 * 
 * HOW TO USE:
 * <Logo theme="light" size="default" />
 * <Logo theme="dark" size="small" />
 * 
 * PROPS:
 * - theme: 'light' or 'dark' - determines which logo variant to show
 * - size: 'small' | 'default' | 'large' - controls logo dimensions
 * - className: Additional CSS classes (optional)
 * 
 * THEME LOGIC:
 * - Light theme (light background) → Shows dark logo for contrast
 * - Dark theme (dark background) → Shows white logo for contrast
 * 
 * FILES:
 * - matchmedic_logo_dark.svg (for light backgrounds)
 * - matchmedic_logo_white.svg (for dark backgrounds)
 */

export default function Logo({ 
  theme = 'light', 
  size = 'small',
  className = "" 
}) {
  
  // Size variants
  const sizeClasses = {
    small: 'h-6',      // 24px height
    default: 'h-8',    // 32px height  
    large: 'h-12'      // 48px height
  };
  
  // Theme-based logo selection
  const logoSrc = theme === 'light' ? logoDark : logoWhite;
    
  const logoAlt = `MatchMedic Logo`;
  
  return (
    <div className={`flex items-center justify-start ${className}`}>
      <img 
        src={logoSrc}
        alt={logoAlt}
        className={`${sizeClasses[size]} w-auto object-contain`}
        // Prevent layout shift during load
        style={{ 
          maxWidth: '200px', // Reasonable max width constraint
          height: 'auto'
        }}
      />
    </div>
  );
}

/**
 * USAGE EXAMPLES:
 * 
 * // In onboarding header (default size, theme-aware)
 * <Logo theme={currentTheme} />
 * 
 * // Small logo in footer
 * <Logo theme="light" size="small" />
 * 
 * // Large logo on welcome screen
 * <Logo theme="dark" size="large" className="mb-8" />
 * 
 * // With custom styling
 * <Logo theme="light" className="opacity-80 hover:opacity-100 transition-opacity" />
 */